import { useMemo, useState } from 'react';
import { Tooltip } from '@mui/material';

import { DataTable, type TableColumn, EditIcon, UsersIcon, TrashIcon } from '@/src/ui';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';
import type { UserBasicDTO } from '../models/userDTOs';

interface UsersTableProps {
    data: Array<UserBasicDTO>;
    setShowCreateEditModal: (val: boolean) => void;
    setShowDeleteModal: (val: boolean) => void;
    modalMode: 'create' | 'edit' | null;
    setModalMode: (val: 'create' | 'edit' | null) => void;
    setSelectedUser: (val: UserBasicDTO | null) => void;
    searchString: string;
    isViewingAdmin: boolean;
    impersonatingUserId: number | null;
    setImpersonatingUserId: (val: number | null) => void;
}

export default function UsersTable(props: UsersTableProps) {
    const [error, setError] = useState<string | null>(null);

    const {
        data,
        setShowCreateEditModal,
        setShowDeleteModal,
        modalMode,
        setModalMode,
        setSelectedUser,
        searchString,
        isViewingAdmin,
        impersonatingUserId,
    } = props;

    const filteredData = useMemo(() => {
        return data.filter((user) => {
            const search = searchString.trim().toLowerCase();
            if (!search) return true;
            return (
                `${user.fName} ${user.lName}`.trim().toLowerCase().includes(search) ||
                user.email?.trim().toLowerCase().includes(search) ||
                user.username.trim().toLowerCase().includes(search)
            );
        });
    }, [data, searchString]);

    const columns = useMemo<TableColumn[]>(() => {
        return [
            {
                dataKey: 'fName',
                header: 'First Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'lName',
                header: 'Last Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'email',
                header: 'Email',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'username',
                header: 'Username',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'actions',
                header: 'Actions',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: UserBasicDTO) => {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <a
                                href={`/impersonate?userId=${row.id}`}
                                title="Impersonate"
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0.6em 1.2em',
                                }}
                                onClick={() => {
                                    if (!modalMode) {
                                        setSelectedUser(row);
                                    }
                                }}
                            >
                                <Tooltip title="Impersonate" arrow>
                                    <span role="img" aria-label="impersonate">
                                        <UsersIcon />
                                    </span>
                                </Tooltip>
                            </a>

                            <Tooltip title="Edit" arrow>
                                <button
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    onClick={() => {
                                        if (!modalMode) {
                                            setSelectedUser(row);
                                            setModalMode('edit');
                                            setShowCreateEditModal(true);
                                        }
                                    }}
                                >
                                    <EditIcon />
                                </button>
                            </Tooltip>

                            <Tooltip title="Delete" arrow>
                                <button
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    onClick={() => {
                                        if (!modalMode) {
                                            setSelectedUser(row);
                                            setShowDeleteModal(true);
                                        }
                                    }}
                                >
                                    <TrashIcon />
                                </button>
                            </Tooltip>
                        </div>
                    );
                },
                hideColumn: isViewingAdmin,
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isViewingAdmin,
        modalMode,
        setModalMode,
        setSelectedUser,
        setShowCreateEditModal,
        setShowDeleteModal,
        impersonatingUserId,
    ]);

    return (
        <>
            {error && (
                <div
                    style={{
                        position: 'fixed',
                        top: '20px',
                        right: '20px',
                        padding: '12px 16px',
                        background: '#f56565',
                        color: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                        zIndex: 1000,
                    }}
                >
                    {error}
                    <button
                        onClick={() => setError(null)}
                        style={{
                            marginLeft: '12px',
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                        }}
                    >
                        ×
                    </button>
                </div>
            )}
            <DataTable
                data={filteredData}
                columns={columns}
                tableName="Users"
                alternatingRowColor
                defaultSortColumn="fName"
                defaultSortDirection="asc"
            />
        </>
    );
}
